export const M5_UNIT_13 = {
  id: "m5-u13-going-out",
  title: "Unit 13: Making Plans",
  topic: "Going out, social activities, and making future arrangements naturally",
  iconType: "calendar-check",
  grammarImage: "/grammar/grammar-m5-u13.jpg",
  grammarText: "This unit teaches you how to talk about future plans using present continuous (I'm meeting friends tomorrow, She's going to a concert next week). This grammar pattern is extremely common in spoken English for definite arrangements—plans you've already decided or scheduled. You'll learn vocabulary for social activities (restaurant, concert, gallery, clubbing), how to invite people, accept invitations, and make polite excuses when you can't go (Sorry, I can't. I'm working late). The grammar emerges naturally through real conversations about weekend plans, dinner reservations, and social events. By the end, you'll handle social planning like a native speaker.",

  sets: [
    // ===================================================================
    // SET 1: GOING OUT VOCABULARY - PLACES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u13-s1",
      title: "Set 1: Places to Go",
      questions: [
        {
          type: "match",
          q: "Match places to what happens there:",
          pairs: [
            { left: "Restaurant", right: "Eat dinner" },
            { left: "Concert hall", right: "Listen to live music" },
            { left: "Art gallery", right: "Look at paintings" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "PLANS: 'Let's go out tonight! Where should we go?' 'How about a ___? I'm hungry!'",
          options: ["restaurant", "concert", "museum"],
          a: "restaurant",
          tip: "Restaurant = place to eat"
        },
        {
          type: "input",
          q: "MUSIC: 'There's a great band playing at the ___ hall tonight.' (music venue)",
          a: "concert",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build invitation:",
          words: ["Do", "you", "want", "to", "go", "to", "the", "cinema?"],
          a: "Do you want to go to the cinema?",
          successtip: "✓ Cinema (UK) = Movie theater (US). Both mean same place!"
        },
        {
          type: "match",
          q: "Match nighttime venues:",
          pairs: [
            { left: "Night club", right: "Dance until late" },
            { left: "Bar", right: "Have drinks with friends" },
            { left: "Theater", right: "Watch a play or show" }
          ],
          a: "completed",
          successtip: "✓ Theatre (UK) / Theater (US). Same pronunciation!"
        },
        {
          type: "mc",
          q: "CULTURE: 'I love looking at paintings. Let's visit the art ___ this weekend.'",
          options: ["gallery", "concert", "club"],
          a: "gallery",
          tip: "Art gallery = museum for art"
        },
        {
          type: "input",
          q: "DANCING: 'After dinner, we can go ___ at that new club downtown.' (activity)",
          a: "dancing",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["clubbing"] },
          successtip: "✓ 'Go dancing' or 'go clubbing' (both mean dance at a nightclub)"
        },
        {
          type: "mc",
          q: "PERFORMANCE: 'The ___ starts at 8pm. The band is amazing live!'",
          options: ["concert", "restaurant", "gallery"],
          a: "concert"
        },
        {
          type: "input",
          q: "BOOKS: 'I joined a ___ club. We meet every month to discuss novels.' (reading group)",
          a: "book",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Traduce: ¿Quieres ir al teatro este fin de semana?",
          words: ["Do", "you", "want", "to", "go", "to", "the", "theater", "this", "weekend?"],
          a: "Do you want to go to the theater this weekend?"
        }
      ]
    },

    // ===================================================================
    // SET 2: GOING OUT VOCABULARY - ACTIVITIES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u13-s2",
      title: "Set 2: Social Activities",
      questions: [
        {
          type: "match",
          q: "Match activities to contexts:",
          pairs: [
            { left: "Meet friends", right: "Arrange to see people" },
            { left: "Go to a party", right: "Celebration/social gathering" },
            { left: "Go bowling", right: "Sport with ball and pins" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "WEEKEND: 'I'm ___ some friends for coffee tomorrow morning.'",
          options: ["meeting", "meets", "meet"],
          a: "meeting",
          tip: "'Am meeting' = present continuous (notice the -ing)"
        },
        {
          type: "input",
          q: "TICKET: 'I need to ___ a ticket for the concert before they sell out.' (purchase)",
          a: "buy",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["get", "purchase"] }
        },
        {
          type: "order",
          q: "Build natural sentence:",
          words: ["We're", "going", "to", "a", "party", "on", "Saturday", "night"],
          a: "We're going to a party on Saturday night",
          successtip: "✓ 'We're going' = we are going (present continuous for future plan)"
        },
        {
          type: "match",
          q: "Match eating vocabulary:",
          pairs: [
            { left: "Menu", right: "List of food at restaurant" },
            { left: "Waiter/Waitress", right: "Person who serves you" },
            { left: "Check (US) / Bill (UK)", right: "Paper showing what you owe" }
          ],
          a: "completed",
          successtip: "✓ Check (US) = Bill (UK). 'Can I have the check/bill, please?'"
        },
        {
          type: "mc",
          q: "RESTAURANT: 'Excuse me, can I see the ___, please? I'm ready to order.'",
          options: ["menu", "bill", "ticket"],
          a: "menu",
          tip: "Menu = list of food and drinks"
        },
        {
          type: "input",
          q: "PAYMENT: 'We finished eating. Let's ask for the ___ and pay.' (US term)",
          a: "check",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["bill"] },
          successtip: "✓ US: check. UK: bill. Both = amount you need to pay"
        },
        {
          type: "mc",
          q: "PERFORMANCE: 'The ___ was incredible! Everyone stood up and clapped.'",
          options: ["show", "menu", "ticket"],
          a: "show",
          tip: "Show = performance (theater, concert, etc.)"
        },
        {
          type: "input",
          q: "MUSIC EVENT: 'There's a big music ___ this summer with 20 different bands!' (outdoor event)",
          a: "festival",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build question about plans:",
          words: ["Are", "you", "going", "to", "the", "concert", "tomorrow?"],
          a: "Are you going to the concert tomorrow?",
          successtip: "✓ 'Are you going' = asking about future plans (present continuous)"
        }
      ]
    },

    // ===================================================================
    // SET 3: FUTURE WITH PRESENT CONTINUOUS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u13-s3",
      title: "Set 3: Talking About Future Plans",
      questions: [
        {
          type: "match",
          q: "Match present continuous uses:",
          pairs: [
            { left: "I'm eating now", right: "Happening RIGHT now" },
            { left: "I'm eating tomorrow at 7pm", right: "Future plan/arrangement" },
            { left: "Both use same form!", right: "Context tells you which" }
          ],
          a: "completed",
          successtip: "✓ Present continuous works for NOW and FUTURE! Time phrase tells you which"
        },
        {
          type: "mc",
          q: "PLANS: 'What are you doing tomorrow?' 'I ___ my parents for lunch.'",
          options: ["'m visiting", "visit", "visited"],
          a: "'m visiting",
          tip: "Tomorrow = future. Use present continuous: am/is/are + -ing"
        },
        {
          type: "input",
          q: "HER PLANS: 'She ___ to a concert next Friday.' (is + verb-ing)",
          a: "is going",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'s going"] },
          tip: "She is going / She's going (both correct)"
        },
        {
          type: "order",
          q: "Build future arrangement:",
          words: ["We're", "meeting", "at", "the", "restaurant", "at", "8pm"],
          a: "We're meeting at the restaurant at 8pm"
        },
        {
          type: "mc",
          q: "QUESTION: 'When ___ you seeing the doctor?' 'On Monday.'",
          options: ["are", "do", "did"],
          a: "are",
          tip: "Question with present continuous: are/is + subject + -ing",
          successtip: "✓ 'When are you seeing...?' (not 'When do you see...?' for future arrangements)"
        },
        {
          type: "input",
          q: "THEIR PLANS: 'They're ___ to Paris next month.' (travel verb -ing)",
          a: "flying",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["going", "traveling", "travelling"] }
        },
        {
          type: "match",
          q: "Match questions to answers:",
          pairs: [
            { left: "What are you doing tonight?", right: "I'm staying home" },
            { left: "Where is she going?", right: "She's going to the gym" },
            { left: "When are they arriving?", right: "They're arriving at 6pm" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "NEGATIVE PLAN: 'I ___ going to the party. I'm too tired.'",
          options: ["'m not", "don't", "didn't"],
          a: "'m not",
          tip: "Negative: am not / is not / are not + -ing",
          successtip: "✓ I'm not going / She isn't going / They aren't going"
        },
        {
          type: "input",
          q: "WEEKEND: 'He's ___ a movie at the cinema on Saturday.' (watch + ing)",
          a: "watching",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["seeing"] }
        },
        {
          type: "order",
          q: "Traduce: ¿Qué estás haciendo mañana por la noche?",
          words: ["What", "are", "you", "doing", "tomorrow", "night?"],
          a: "What are you doing tomorrow night?",
          successtip: "✓ In Spanish you might say 'vas a hacer' but English often uses present continuous!"
        }
      ]
    },

    // ===================================================================
    // SET 4: TIME EXPRESSIONS WITH FUTURE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u13-s4",
      title: "Set 4: When Are You Going?",
      questions: [
        {
          type: "match",
          q: "Match time prepositions:",
          pairs: [
            { left: "On + specific day", right: "on Monday, on June 5th" },
            { left: "In + month/season/year", right: "in June, in summer, in 2026" },
            { left: "At + time", right: "at 7pm, at midnight, at noon" }
          ],
          a: "completed",
          successtip: "✓ ON specific days. IN longer periods. AT clock times"
        },
        {
          type: "mc",
          q: "SPECIFIC DAY: 'I'm meeting Sarah ___ Tuesday.'",
          options: ["on", "in", "at"],
          a: "on",
          tip: "Days of week use 'on': on Monday, on Tuesday..."
        },
        {
          type: "input",
          q: "MONTH: 'We're going to Italy ___ July.' (preposition)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Months use 'in': in June, in December..."
        },
        {
          type: "order",
          q: "Build sentence with date:",
          words: ["The", "concert", "is", "on", "May", "15th"],
          a: "The concert is on May 15th",
          successtip: "✓ Specific dates use 'on': on May 15th, on January 1st"
        },
        {
          type: "mc",
          q: "TIME: 'The movie starts ___ 8 o'clock.'",
          options: ["at", "on", "in"],
          a: "at",
          tip: "Clock times use 'at': at 7pm, at noon, at midnight"
        },
        {
          type: "input",
          q: "SEASON: 'I'm visiting my family ___ summer.' (preposition)",
          a: "in",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Seasons use 'in': in spring, in summer, in fall/autumn, in winter"
        },
        {
          type: "match",
          q: "Match complete time expressions:",
          pairs: [
            { left: "I'm leaving on Friday", right: "Specific day" },
            { left: "I'm leaving at 3pm", right: "Specific time" },
            { left: "I'm leaving in March", right: "Month" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "COMBINING: 'We're having dinner ___ Saturday ___ 7pm.'",
          options: ["on ... at", "at ... on", "in ... at"],
          a: "on ... at",
          tip: "Day = on. Time = at. Can combine!",
          successtip: "✓ 'On Saturday at 7pm' = day + time (two prepositions!)"
        },
        {
          type: "input",
          q: "WEEKEND: 'Are you doing anything ___ the weekend?' (preposition)",
          a: "on",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["this", "over"] },
          tip: "'On the weekend' (US) or 'at the weekend' (UK)",
          successtip: "✓ US: 'on the weekend'. UK: 'at the weekend'. Both correct!"
        },
        {
          type: "order",
          q: "Complete time phrase:",
          words: ["I'm", "flying", "to", "London", "on", "June", "12th", "at", "9am"],
          a: "I'm flying to London on June 12th at 9am",
          successtip: "✓ Date + time: 'on June 12th at 9am' (two prepositions work together!)"
        }
      ]
    },

    // ===================================================================
    // SET 5: MAKING INVITATIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u13-s5",
      title: "Set 5: Inviting People",
      questions: [
        {
          type: "match",
          q: "Match invitation phrases:",
          pairs: [
            { left: "Do you want to...?", right: "Direct invitation" },
            { left: "Would you like to...?", right: "Polite invitation" },
            { left: "Are you free to...?", right: "Checking availability" }
          ],
          a: "completed",
          successtip: "✓ All three work! 'Would you like...' = most polite"
        },
        {
          type: "mc",
          q: "CASUAL: 'Do you want to ___ out for dinner tonight?'",
          options: ["go", "going", "goes"],
          a: "go",
          tip: "After 'want to' use base form (infinitive)",
          successtip: "✓ 'Want to' + base verb: want to go, want to see, want to eat"
        },
        {
          type: "input",
          q: "POLITE: 'Would you ___ to come to my party on Saturday?' (like)",
          a: "like",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Would you like to...' = polite invitation"
        },
        {
          type: "order",
          q: "Build polite invitation:",
          words: ["Would", "you", "like", "to", "join", "us", "for", "lunch?"],
          a: "Would you like to join us for lunch?",
          successtip: "✓ 'Join us' = come with us (common invitation phrase)"
        },
        {
          type: "mc",
          q: "CHECKING: 'Are you ___ on Friday? We're going to the movies.'",
          options: ["free", "busy", "working"],
          a: "free",
          tip: "'Are you free?' = are you available?",
          successtip: "✓ 'Are you free?' asks availability before inviting"
        },
        {
          type: "input",
          q: "SUGGESTING: 'How ___ going to the concert together?' (about)",
          a: "about",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'How about...' = suggestion/invitation",
          successtip: "✓ 'How about + -ing': How about going...? How about meeting...?"
        },
        {
          type: "match",
          q: "Match invitations to responses:",
          pairs: [
            { left: "Do you want to come?", right: "Yes, I'd love to!" },
            { left: "Are you free tonight?", right: "Sorry, I'm busy" },
            { left: "Would you like to join us?", right: "That sounds great!" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "SUGGESTION: 'Let's ___ to that new restaurant. I heard it's amazing!'",
          options: ["go", "going", "to go"],
          a: "go",
          tip: "'Let's' + base form (no 'to')",
          successtip: "✓ 'Let's go' = suggestion to do something together"
        },
        {
          type: "input",
          q: "ENTHUSIASM: 'I'm having a party Saturday. Can you ___?' (attend)",
          a: "come",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["make it", "attend"] }
        },
        {
          type: "order",
          q: "Traduce: ¿Te gustaría venir con nosotros al teatro?",
          words: ["Would", "you", "like", "to", "come", "with", "us", "to", "the", "theater?"],
          a: "Would you like to come with us to the theater?",
          successtip: "✓ 'Come with us' = ven con nosotros (natural English phrase)"
        }
      ]
    },

    // ===================================================================
    // SET 6: ACCEPTING INVITATIONS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u13-s6",
      title: "Set 6: Saying Yes!",
      questions: [
        {
          type: "match",
          q: "Match enthusiasm levels:",
          pairs: [
            { left: "Yes, sure!", right: "Casual acceptance" },
            { left: "I'd love to!", right: "Enthusiastic acceptance" },
            { left: "That sounds great!", right: "Positive interest" }
          ],
          a: "completed",
          successtip: "✓ More enthusiasm = warmer response! 'I'd love to!' > 'Sure'"
        },
        {
          type: "mc",
          q: "ENTHUSIASTIC: 'Would you like to come to dinner?' Best warm response:",
          options: ["I'd love to!", "OK.", "Maybe."],
          a: "I'd love to!",
          tip: "'I'd love to' = I would love to (enthusiastic!)",
          successtip: "✓ 'I'd love to!' makes people happy. Better than just 'Yes' or 'OK'"
        },
        {
          type: "input",
          q: "POSITIVE: 'Do you want to see a movie?' 'That ___ great! What time?' (sounds)",
          a: "sounds",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'That sounds great' = I like that idea"
        },
        {
          type: "order",
          q: "Build enthusiastic acceptance:",
          words: ["Yes,", "I'd", "love", "to!", "What", "time?"],
          a: "Yes, I'd love to! What time?",
          successtip: "✓ Accept + ask detail = shows real interest!"
        },
        {
          type: "mc",
          q: "CASUAL: 'Are you free for coffee?' Casual yes:",
          options: ["Sure! When?", "Perhaps.", "I'll think about it."],
          a: "Sure! When?",
          tip: "'Sure' = casual yes (friendly and easy)",
          successtip: "✓ 'Sure!' = casual, friendly acceptance. Very common!"
        },
        {
          type: "input",
          q: "AGREEMENT: 'Let's go bowling!' 'Sounds ___!' (fun)",
          a: "fun",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["good", "great", "perfect"] },
          tip: "'Sounds fun!' = I think that will be fun"
        },
        {
          type: "match",
          q: "Match acceptance + follow-up:",
          pairs: [
            { left: "I'd love to! What time?", right: "Accept + ask time" },
            { left: "Sure! Where are we meeting?", right: "Accept + ask place" },
            { left: "Sounds great! Should I bring anything?", right: "Accept + offer help" }
          ],
          a: "completed",
          successtip: "✓ Accept + question = natural conversation flow"
        },
        {
          type: "mc",
          q: "DEFINITE: 'Would you like to join us for dinner?' Strong yes:",
          options: ["Absolutely! I'd love to!", "Maybe.", "I don't know."],
          a: "Absolutely! I'd love to!",
          tip: "'Absolutely!' = definitely yes!",
          successtip: "✓ 'Absolutely!' shows strong enthusiasm. Very positive!"
        },
        {
          type: "input",
          q: "CONFIRMATION: 'Concert on Friday?' 'Yes! I'm ___ forward to it!' (looking)",
          a: "looking",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Looking forward to it' = excited about it",
          successtip: "✓ 'Looking forward to it' = perfect phrase after accepting invitation!"
        },
        {
          type: "order",
          q: "Natural enthusiastic response:",
          words: ["That", "sounds", "amazing!", "Count", "me", "in!"],
          a: "That sounds amazing! Count me in!",
          successtip: "✓ 'Count me in!' = I definitely want to join! Very enthusiastic phrase!"
        }
      ]
    },

    // ===================================================================
    // SET 7: MAKING EXCUSES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u13-s7",
      title: "Set 7: Politely Saying No",
      questions: [
        {
          type: "match",
          q: "Polite refusal structure:",
          pairs: [
            { left: "Sorry, I can't", right: "Apologize first" },
            { left: "I'm working late", right: "Give reason (excuse)" },
            { left: "Maybe next time?", right: "Keep relationship positive" }
          ],
          a: "completed",
          successtip: "✓ Sorry + reason + future = polite refusal pattern"
        },
        {
          type: "mc",
          q: "POLITE NO: 'Do you want to come to dinner?' 'Sorry, I ___. I'm busy that night.'",
          options: ["can't", "don't", "won't"],
          a: "can't",
          tip: "'Sorry, I can't' = polite way to say no",
          successtip: "✓ Always say 'Sorry' first! Makes refusal much more polite"
        },
        {
          type: "input",
          q: "EXCUSE: 'Can you come Friday?' 'I can't. I'm ___ late that day.' (work + ing)",
          a: "working",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Present continuous for future plan = excuse",
          successtip: "✓ 'I'm working' / 'I'm visiting family' = present continuous makes excuse clear"
        },
        {
          type: "order",
          q: "Build polite refusal:",
          words: ["Sorry,", "I", "can't.", "I'm", "meeting", "my", "parents."],
          a: "Sorry, I can't. I'm meeting my parents.",
          successtip: "✓ Pattern: Sorry + can't + reason (present continuous)"
        },
        {
          type: "mc",
          q: "KEEPING DOOR OPEN: After saying no, which is most friendly?",
          options: ["Maybe next time?", "No.", "I'm busy."],
          a: "Maybe next time?",
          tip: "'Maybe next time' keeps friendship positive",
          successtip: "✓ 'Maybe next time?' shows you want to hang out, just not now. Important!"
        },
        {
          type: "input",
          q: "ALTERNATIVE: 'Can't make it Friday. How about ___?' (another day)",
          a: "Saturday",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Sunday", "next week", "another day", "Thursday", "Monday"] },
          tip: "Suggest alternative = shows you want to meet",
          successtip: "✓ Suggesting another time = polite and shows real interest!"
        },
        {
          type: "match",
          q: "Match excuse types:",
          pairs: [
            { left: "I'm working late", right: "Work excuse" },
            { left: "I'm visiting family", right: "Family excuse" },
            { left: "I'm not feeling well", right: "Health excuse" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ALREADY BUSY: 'Concert Saturday?' 'I can't. I ___ to another concert that night!'",
          options: ["'m going", "go", "went"],
          a: "'m going",
          tip: "Already have plans = present continuous",
          successtip: "✓ 'I'm going' / 'I'm seeing' / 'I'm doing' = I already have plans"
        },
        {
          type: "input",
          q: "REAL EXCUSE: 'Sorry, I can't come. I'm ___ my sister move.' (help + ing)",
          a: "helping",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Present continuous = future arrangement/excuse"
        },
        {
          type: "order",
          q: "Complete polite refusal with alternative:",
          words: ["Sorry,", "I", "can't", "on", "Friday.", "How", "about", "Saturday?"],
          a: "Sorry, I can't on Friday. How about Saturday?",
          successtip: "✓ PERFECT! Refuse politely + offer alternative = maintains relationship! 🤝"
        }
      ]
    },

    // ===================================================================
    // SET 8: MORE EXCUSES & ALTERNATIVES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u13-s8",
      title: "Set 8: Real-Life Excuses",
      questions: [
        {
          type: "match",
          q: "Match excuse situations:",
          pairs: [
            { left: "I'm studying for an exam", right: "Academic excuse" },
            { left: "I'm taking my dog to the vet", right: "Pet/responsibility excuse" },
            { left: "I'm having dinner with my boss", right: "Professional excuse" }
          ],
          a: "completed",
          successtip: "✓ Specific excuses sound more believable than vague ones!"
        },
        {
          type: "mc",
          q: "BELIEVABLE: Which sounds most natural?",
          options: ["Sorry, I'm visiting my grandmother in the hospital", "Sorry, I'm busy", "Sorry, I can't"],
          a: "Sorry, I'm visiting my grandmother in the hospital",
          tip: "Specific = more believable and polite",
          successtip: "✓ Specific details make excuses sound genuine, not like lies"
        },
        {
          type: "input",
          q: "EXAM EXCUSE: 'I can't go out. I'm ___ for my final exam.' (study + ing)",
          a: "studying",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["preparing", "revising"] }
        },
        {
          type: "order",
          q: "Build work excuse:",
          words: ["Sorry,", "I", "can't.", "I'm", "working", "overtime", "this", "week."],
          a: "Sorry, I can't. I'm working overtime this week.",
          successtip: "✓ 'Working overtime' = working extra hours (common excuse!)"
        },
        {
          type: "mc",
          q: "RAIN CHECK: 'Can I take a rain check?' means:",
          options: ["Can we do it another time?", "Is it going to rain?", "No, never."],
          a: "Can we do it another time?",
          tip: "'Rain check' = postpone to another time (idiom)",
          successtip: "✓ 'Take a rain check' = idiomatic way to say 'maybe another time'. Very common!"
        },
        {
          type: "input",
          q: "SOFTENING: 'I'd love to, ___ I'm already going to another party.' (but)",
          a: "but",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'I'd love to, but...' = softens refusal",
          successtip: "✓ 'I'd love to, but...' = shows you want to, circumstances prevent you. Polite!"
        },
        {
          type: "match",
          q: "Match refusal phrases to tone:",
          pairs: [
            { left: "Sorry, I can't", right: "Polite" },
            { left: "I'd love to, but I can't", right: "Very polite" },
            { left: "No", right: "Direct (possibly rude)" }
          ],
          a: "completed",
          successtip: "✓ Never just say 'No'! Always add 'Sorry' or 'I'd love to, but...'"
        },
        {
          type: "mc",
          q: "FAMILY EXCUSE: 'I can't come. I'm ___ my parents at the airport.'",
          options: ["picking up", "pick up", "picked up"],
          a: "picking up",
          tip: "Present continuous: am/is/are + -ing",
          successtip: "✓ 'Picking up' = going to collect someone. Common excuse phrase!"
        },
        {
          type: "input",
          q: "SUGGESTING ALTERNATIVE: 'I can't tonight. Are you ___ tomorrow?' (available)",
          a: "free",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["available", "busy"] }
        },
        {
          type: "order",
          q: "Perfect polite refusal with interest:",
          words: ["I'd", "love", "to,", "but", "I'm", "already", "seeing", "someone", "else.", "Next", "week?"],
          a: "I'd love to, but I'm already seeing someone else. Next week?",
          successtip: "✓ MASTERFUL! Love to + but + excuse + alternative = perfect social English! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMPLEX PLANNING CONVERSATIONS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u13-s9",
      title: "Set 9: Planning Together",
      questions: [
        {
          type: "match",
          q: "Planning conversation stages:",
          pairs: [
            { left: "Invitation", right: "Do you want to...?" },
            { left: "Check details", right: "What time? Where?" },
            { left: "Confirm", right: "Great! See you then!" }
          ],
          a: "completed",
          successtip: "✓ Natural planning: invite → discuss details → confirm"
        },
        {
          type: "mc",
          q: "COORDINATING: 'What time ___ meeting?' 'How about 7pm?'",
          options: ["are we", "do we", "did we"],
          a: "are we",
          tip: "Future plans = present continuous",
          successtip: "✓ 'What time are we meeting?' (not 'do we meet')"
        },
        {
          type: "input",
          q: "LOCATION: 'Where ___ we meeting? At the restaurant or the theater?' (are)",
          a: "are",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "order",
          q: "Build planning dialogue:",
          words: ["Let's", "meet", "at", "7pm.", "Where", "should", "we", "go?"],
          a: "Let's meet at 7pm. Where should we go?",
          successtip: "✓ Time first, then location. Natural planning order!"
        },
        {
          type: "mc",
          q: "GROUP PLANS: 'Who else ___ coming to dinner?' 'Just us and Maria.'",
          options: ["is", "are", "does"],
          a: "is",
          tip: "'Who else' = singular (even if answer is plural)",
          successtip: "✓ 'Who is coming?' but answer can be plural: 'John and Mary are coming'"
        },
        {
          type: "input",
          q: "CONFIRMATION: 'So we're ___ at the café at 6, right?' (meet + ing)",
          a: "meeting",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "match",
          q: "Match confirmation phrases:",
          pairs: [
            { left: "See you there!", right: "Location confirmed" },
            { left: "See you then!", right: "Time confirmed" },
            { left: "See you at 7!", right: "Specific time" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "CHANGING PLANS: 'Actually, I can't make 7pm. ___ 8pm work?'",
          options: ["Does", "Is", "Are"],
          a: "Does",
          tip: "'Does 8pm work?' = Is 8pm OK?",
          successtip: "✓ 'Does [time] work?' = common way to suggest alternative time"
        },
        {
          type: "input",
          q: "AGREEMENT: '8pm ___ perfect! See you then!' (is)",
          a: "is",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'s", "sounds", "works"] },
          tip: "'Sounds perfect' or 'is perfect' both work"
        },
        {
          type: "order",
          q: "Complete planning exchange:",
          words: ["Let's", "meet", "at", "the", "restaurant", "at", "7.", "Perfect!", "See", "you", "there!"],
          a: "Let's meet at the restaurant at 7. Perfect! See you there!",
          successtip: "✓ Complete planning cycle: suggest → agree → confirm! Natural flow! 📅"
        }
      ]
    },

    // ===================================================================
    // SET 10: ADVANCED SOCIAL PLANNING (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u13-s10",
      title: "Set 10: Social Mastery",
      questions: [
        {
          type: "match",
          q: "Social planning situations:",
          pairs: [
            { left: "Making group plans", right: "Who else is coming?" },
            { left: "Last-minute invitation", right: "Are you free now?" },
            { left: "Standing plans", right: "We're meeting every Tuesday" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "LAST MINUTE: 'Hey, are you free right now? We're ___ for coffee.'",
          options: ["going", "go", "went"],
          a: "going",
          tip: "'Right now' + present continuous = happening very soon",
          successtip: "✓ Present continuous works for plans happening RIGHT now or very soon!"
        },
        {
          type: "input",
          q: "REGULAR PLANS: 'We're ___ at the gym every Monday at 6pm.' (meet + ing)",
          a: "meeting",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["going", "training"] },
          tip: "Present continuous can describe regular future arrangements",
          successtip: "✓ 'We're meeting every Monday' = repeated future arrangement (also correct!)"
        },
        {
          type: "order",
          q: "Build tentative plan:",
          words: ["I'm", "thinking", "of", "going", "to", "the", "movies.", "Want", "to", "join?"],
          a: "I'm thinking of going to the movies. Want to join?",
          successtip: "✓ 'Thinking of going' = considering it. Casual invitation!"
        },
        {
          type: "mc",
          q: "CHECKING PLANS: 'Are we still ___ tomorrow, or did you cancel?'",
          options: ["meeting", "meet", "met"],
          a: "meeting",
          tip: "'Are we still meeting?' = confirming existing plan"
        },
        {
          type: "input",
          q: "TRANSPORTATION: 'How are you ___ there? I can give you a ride.' (get + ing)",
          a: "getting",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'How are you getting there?' = transport question",
          successtip: "✓ 'Give you a ride' (US) = 'Give you a lift' (UK). Offer transport!"
        },
        {
          type: "match",
          q: "Match planning complications:",
          pairs: [
            { left: "Running late", right: "I'm running 10 minutes late" },
            { left: "Can't find place", right: "Where exactly are we meeting?" },
            { left: "Need to reschedule", right: "Can we do another day?" }
          ],
          a: "completed",
          successtip: "✓ Real-life problems need real-life English!"
        },
        {
          type: "mc",
          q: "RUNNING LATE: 'Sorry! I'm running late. I'll be there ___.'",
          options: ["in 10 minutes", "before 10 minutes", "10 minutes ago"],
          a: "in 10 minutes",
          tip: "'In 10 minutes' = 10 minutes from now",
          successtip: "✓ 'Running late' = arriving later than planned (common phrase!)"
        },
        {
          type: "input",
          q: "RESCHEDULING: 'Something came up. Can we ___ our plans?' (change)",
          a: "reschedule",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["change", "postpone", "move"] },
          tip: "'Something came up' = unexpected thing happened",
          successtip: "✓ 'Something came up' = polite way to say you need to cancel/reschedule"
        },
        {
          type: "order",
          q: "MASTERY: Complete natural planning conversation:",
          words: ["Are", "you", "free", "Friday?", "I'm", "having", "a", "party.", "I'd", "love", "to!", "What", "time?", "Around", "8.", "See", "you", "then!"],
          a: "Are you free Friday? I'm having a party. I'd love to! What time? Around 8. See you then!",
          successtip: "✓ PERFECT! You've mastered social planning! Invite → Accept → Details → Confirm. Native-level conversation! 🎉👥"
        }
      ]
    }
  ],

readingSet: {
  id: "m5-u13-reading",
  title: "Reading: Making Dinner Plans",
  category: "reading",

  questions: [
     {  type: "passage",
      passage: `**The Plan**

**Mia:** Hey Tom! Are you free on Saturday evening?
**Tom:** Let me check... Yes, I am! What's up?
**Mia:** I'm going to that new Italian restaurant downtown. Would you like to come?
**Tom:** I'd love to! What time are you going?
**Mia:** I'm meeting Sarah at 7pm. You can join us!
**Tom:** Sounds great! Where exactly is the restaurant?
**Mia:** It's on Main Street, next to the movie theater.
**Tom:** Perfect! I know where that is. See you at 7!`
    },
    {
      type: "mc",
      q: "On what day of the week are they planning to meet?",
      options: ["Saturday", "Friday", "Sunday"],
      a: "Saturday",
      successtip: "✓ SATURDAY! A classic night for dinner plans. 🍕"
    },
    {
      type: "input",
      q: "At what time does Mia say she is meeting Sarah?",
      a: "7pm",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["7", "seven", "7 pm", "19:00"] },
      successtip: "✓ 7:00 PM! Just in time for dinner. 🕖"
    },
    {
      type: "mc",
      q: "Which grammar form does Mia use when she says 'I'm going' and 'I'm meeting'?",
      options: ["Present continuous for future arrangements", "Simple present", "Will + verb"],
      a: "Present continuous for future arrangements",
      successtip: "✓ 'I'm going' and 'I'm meeting' = present continuous! We use it for fixed plans in the future. 📅"
    },
    {
      type: "passage",
      passage: `**A Change of Time**

**Tom:** Actually, Mia, I have a problem. I'm working until 6:30 on Saturday.
**Mia:** Oh no! Can you make it by 7?
**Tom:** It might be tight. How about 7:30 instead?
**Mia:** Let me ask Sarah... Sarah says 7:30 works for her too!
**Tom:** Great! Sorry for the inconvenience.
**Mia:** No problem! We're flexible. See you at 7:30!`
    },
    {
      type: "mc",
      q: "Why does Tom need to push the meeting time back?",
      options: ["He's working until 6:30", "He doesn't like Italian food", "He has another party"],
      a: "He's working until 6:30",
      successtip: "✓ WORK CONFLICT!\nHe needs more time to get there after work. 💼"
    },
    {
      type: "input",
      q: "What excuse does Tom give using the present continuous? 'I'm ___.'",
      a: "working",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["working until 6:30", "working late"] },
      successtip: "✓ 'I'm working' = present continuous as an excuse for why he can't make it at 7:00! 🛠️"
    },
    {
      type: "mc",
      q: "What is the final time they all agreed on?",
      options: ["7:30pm", "7:00pm", "8:00pm"],
      a: "7:30pm",
      successtip: "✓ 7:30 PM! Everyone is happy with the change. ✅"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Final Details (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Confirming the Details**

**Sarah:** Hi guys! I'm so excited about Saturday! Should we meet at the restaurant or somewhere else first?
**Mia:** Let's meet directly at the restaurant. It's easier.
**Tom:** Sounds good. By the way, are we splitting the check or is someone treating us?
**Mia:** Let's split it. It's more fair that way.
**Sarah:** Works for me! Oh, I'm bringing my sister. Is that OK?
**Mia:** Of course! The more the merrier!
**Tom:** Perfect. I'm really looking forward to it!`
    },
    {
      type: "mc",
      q: "Where is the final meeting point?",
      options: ["At the restaurant", "At Mia's house", "At the movie theater"],
      a: "At the restaurant",
      successtip: "✓ DIRECTLY AT THE RESTAURANT!\nNo need to meet anywhere else first. 🍝"
    },
    {
      type: "input",
      q: "How are they going to pay for the meal? (To ___ the check)",
      a: "split",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["splitting", "split the check", "share"] },
      successtip: "✓ 'Split the check' = everyone pays their part. A very useful phrase! 💸"
    },
    {
      type: "mc",
      q: "Who is the extra guest Sarah is inviting?",
      options: ["Her sister", "Her brother", "Her boss"],
      a: "Her sister",
      successtip: "✓ HER SISTER!\nThe more the merrier! 👯‍♀️"
    },
    {
      type: "mc",
      q: "COMPREHENSION: What does this conversation primarily demonstrate?",
      options: ["Natural planning with present continuous and adjusting details", "Formal business English", "Past tense storytelling"],
      a: "Natural planning with present continuous and adjusting details",
      successtip: "✓ PERFECT! This is exactly how native speakers coordinate plans: invite → excuse → adjust → confirm! 🏆"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u13-review",
    title: "Unit 13 Review: Social Planning Mastery",
    questions: [
      {
        type: "match",
        q: "Review: Match places to activities:",
        pairs: [
          { left: "Restaurant", right: "Have dinner" },
          { left: "Concert hall", right: "Listen to music" },
          { left: "Night club", right: "Go dancing" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "FUTURE PLANS: 'I ___ my friends tomorrow at 6pm.'",
        options: ["'m meeting", "meet", "met"],
        a: "'m meeting",
        tip: "Present continuous for future arrangements"
      },
      {
        type: "input",
        q: "TIME: 'The concert is ___ Saturday ___ 8pm.' (two prepositions)",
        a: "on at",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["on ... at"] },
        tip: "On + day, at + time"
      },
      {
        type: "mc",
        q: "INVITATION: 'Would you ___ to come to my party?'",
        options: ["like", "want", "can"],
        a: "like",
        tip: "'Would you like to...' = polite invitation"
      },
      {
        type: "input",
        q: "ACCEPTANCE: 'Do you want to go out?' '___ love to!' (I would = contraction)",
        a: "I'd",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["I would"] }
      },
      {
        type: "mc",
        q: "EXCUSE: 'Can you come Friday?' 'Sorry, I ___. I'm working late.'",
        options: ["can't", "don't", "won't"],
        a: "can't",
        tip: "'Sorry, I can't' = polite refusal"
      },
      {
        type: "input",
        q: "PRESENT CONTINUOUS EXCUSE: 'I can't come. I'm ___ my grandmother.' (visit + ing)",
        a: "visiting",
        accept: { ignoreCase: true, ignorePunctuation: true }
      },
      {
        type: "mc",
        q: "SUGGESTING ALTERNATIVE: 'I can't tonight. ___ tomorrow?'",
        options: ["How about", "Why", "When"],
        a: "How about",
        tip: "'How about...' = suggestion"
      },
      {
        type: "order",
        q: "Build complete polite refusal:",
        words: ["Sorry,", "I", "can't.", "I'm", "studying.", "Maybe", "next", "week?"],
        a: "Sorry, I can't. I'm studying. Maybe next week?"
      },
      {
        type: "mc",
        q: "FINAL MASTERY: Best response to invitation?",
        options: ["I'd love to! What time?", "Maybe.", "OK."],
        a: "I'd love to! What time?",
        successtip: "✓ CONGRATULATIONS! You can now: make plans, invite people, accept enthusiastically, refuse politely, and reschedule naturally! You're ready for real English social life! 🎉👥📅"
      }
    ]
  }
};
